import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { HOME } from "@/constants/testIds";

/**
 * Image-based rotating hero.
 * Mobile: <img> uses object-contain so the FULL image is always visible —
 *         no cropping, ever. Empty space is filled by the brand-navy background.
 * Desktop (lg+): switches to object-cover for a full-bleed cinematic feel
 *         because desktop aspect ratio matches landscape source images.
 * A subtle side gradient keeps text readable in either mode.
 */
const STATES = [
  { phrase: "Hire Better Talent",          img: "https://images.unsplash.com/photo-1758518731468-98e90ffd7430?auto=format&fit=crop&w=1600&q=85" },
  { phrase: "Build Stronger Teams",        img: "https://images.unsplash.com/photo-1758691736975-9f7f643d178e?auto=format&fit=crop&w=1600&q=85" },
  { phrase: "Discover IT Professionals",   img: "https://images.pexels.com/photos/7580648/pexels-photo-7580648.jpeg?auto=compress&cs=tinysrgb&w=1600" },
  { phrase: "Find Non-IT Talent",          img: "https://images.unsplash.com/photo-1758518729685-f88df7890776?auto=format&fit=crop&w=1600&q=85" },
  { phrase: "Execute Executive Search",    img: "https://images.pexels.com/photos/7580644/pexels-photo-7580644.jpeg?auto=compress&cs=tinysrgb&w=1600" },
  { phrase: "Manage Bulk Hiring",          img: "https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=1600&q=85" },
  { phrase: "Screen Candidates Better",    img: "https://images.pexels.com/photos/5439137/pexels-photo-5439137.jpeg?auto=compress&cs=tinysrgb&w=1600" },
  { phrase: "Support Campus Hiring",       img: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1600&q=85" },
  { phrase: "Deliver HR Consulting",       img: "https://images.unsplash.com/photo-1577962917302-cd874c4e31d2?auto=format&fit=crop&w=1600&q=85" },
  { phrase: "Create Better Opportunities", img: "https://images.pexels.com/photos/7581111/pexels-photo-7581111.jpeg?auto=compress&cs=tinysrgb&w=1600" },
];

const INTERVAL = 5000;

export default function DynamicHero() {
  const [index, setIndex] = useState(0);
  const [phraseState, setPhraseState] = useState("active");

  // Preload
  useEffect(() => {
    STATES.forEach((s) => {
      const img = new Image();
      img.src = s.img;
    });
  }, []);

  // Rotate
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let cancelled = false;
    const timer = setInterval(() => {
      setPhraseState("leave");
      setTimeout(() => {
        if (cancelled) return;
        setIndex((i) => (i + 1) % STATES.length);
        setPhraseState("enter");
        setTimeout(() => {
          if (cancelled) return;
          setPhraseState("active");
        }, 30);
      }, reduced ? 200 : 500);
    }, INTERVAL);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  return (
    <section
      className="relative w-full overflow-hidden bg-brand-navy"
      aria-label="Recruitment hero"
    >
      {/* --------- MOBILE HERO (< lg) --------- */}
      {/* Uses object-contain so the ENTIRE person/subject is always visible on
          phones — no faces or legs get cropped. Small letterbox area (if any)
          blends into the surrounding brand-navy so it is invisible. Content
          sits below on solid navy for perfect readability. */}
      <div className="lg:hidden flex flex-col">
        <div className="relative w-full aspect-[4/3] sm:aspect-[16/10] bg-brand-navy overflow-hidden">
          {STATES.map((s, i) => (
            <img
              key={i}
              src={s.img}
              alt={s.phrase}
              className="absolute inset-0 w-full h-full object-contain transition-opacity duration-[1200ms] ease-in-out"
              style={{ opacity: i === index ? 1 : 0, objectPosition: "center center" }}
              loading={i === 0 ? "eager" : "lazy"}
              draggable={false}
            />
          ))}
          {/* soft bottom fade so image blends into content block below */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-b from-transparent to-brand-navy" />
        </div>

        <div className="bg-brand-navy px-5 py-10 sm:px-6 sm:py-12">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/25 text-white/90 text-[10px] font-semibold tracking-[0.22em] uppercase">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-teal animate-pulse" />
            Pune-based IT &amp; Non-IT Recruitment
          </span>

          <h1 className="mt-5 font-heading font-extrabold text-white text-3xl sm:text-4xl leading-[1.08] tracking-tight">
            <span className={`hero-phrase ${phraseState} text-brand-teal`} key={STATES[index].phrase}>
              {STATES[index].phrase}
            </span>
            <br />
            <span className="text-white">with Ascend Tech Solutions</span>
          </h1>

          <p className="mt-4 text-sm sm:text-base text-white/95 leading-relaxed max-w-lg font-medium">
            A trusted recruitment &amp; staffing partner for IT, Non-IT, BPO,
            executive search and bulk hiring — helping ambitious teams across
            India hire faster and hire right.
          </p>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link to="/contact?type=employer" data-testid={HOME.hireCta} className="btn-primary bg-brand-royal hover:bg-brand-strong">
              Hire Talent <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/services" data-testid={HOME.servicesCta} className="btn-ghost-light">
              Explore Services
            </Link>
          </div>
        </div>
      </div>

      {/* --------- DESKTOP HERO (lg+) --------- */}
      <div className="hidden lg:block relative min-h-[780px]">
        <div className="absolute inset-0">
          {STATES.map((s, i) => (
            <img
              key={i}
              src={s.img}
              alt=""
              aria-hidden="true"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ease-in-out ${i === index ? "is-active-zoom" : ""}`}
              style={{ opacity: i === index ? 1 : 0, objectPosition: "center 30%" }}
              draggable={false}
            />
          ))}
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(10,20,45,0.82) 0%, rgba(10,20,45,0.62) 45%, rgba(10,20,45,0.32) 100%)" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,20,45,0.35) 0%, rgba(10,20,45,0.05) 40%, rgba(10,20,45,0.55) 100%)" }} />
        </div>

        <div className="container-brand relative z-10 flex flex-col justify-center min-h-[780px] py-28 md:py-32">
          <span className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full bg-white/10 border border-white/25 backdrop-blur-sm text-white/90 text-xs font-semibold tracking-[0.22em] uppercase">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-teal animate-pulse" />
            Pune-based IT &amp; Non-IT Recruitment
          </span>

          <h1 className="mt-6 font-heading font-extrabold text-white text-5xl lg:text-7xl leading-[1.05] tracking-tight max-w-5xl">
            <span className={`hero-phrase ${phraseState} text-brand-teal`} key={STATES[index].phrase}>
              {STATES[index].phrase}
            </span>
            <br />
            <span className="text-white">with Ascend Tech Solutions</span>
          </h1>

          <p className="mt-6 max-w-2xl text-lg text-white/95 leading-relaxed font-medium">
            A trusted recruitment &amp; staffing partner for IT, Non-IT, BPO,
            executive search and bulk hiring — helping ambitious teams across
            India hire faster, hire smarter, and hire right.
          </p>

          <div className="mt-8 flex gap-4">
            <Link to="/contact?type=employer" data-testid={`${HOME.hireCta}-lg`} className="btn-primary bg-brand-royal hover:bg-brand-strong">
              Hire Talent <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/services" data-testid={`${HOME.servicesCta}-lg`} className="btn-ghost-light">
              Explore Services
            </Link>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-white/75 text-sm">
            <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-brand-teal" /> IT Hiring</span>
            <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-brand-teal" /> Non-IT Hiring</span>
            <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-brand-teal" /> Executive Search</span>
            <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-brand-teal" /> Bulk &amp; Campus Hiring</span>
          </div>
        </div>
      </div>
    </section>
  );
}
